<template>
  <v-dialog
    :value="dialog"
    :max-width="maxWidth"
    class="chronology-editor"
    scrollable
  >
    <v-card>
      <v-card-title class="body-2 pa-2">
        クロノロジー{{ !data ? "登録" : "編集" }}
      </v-card-title>
      <v-card-text class="px-3 py-1" ref="chronologyContent">
        <div class="d-flex">
          <v-select
            class="mr-2"
            :items="categories"
            label="カテゴリ"
            style="font-size: 14px; max-width: 225px"
            v-model="target.category"
          />
          <v-select
            class="flex-grow-1 ml-2"
            :items="sections"
            label="情報種別"
            style="font-size: 14px"
            v-model="target.section"
          />
        </div>

        <div class="d-flex">
          <v-text-field
            class="flex-grow-1 mr-2"
            style="font-size: 14px"
            label="入力者"
            v-model="target.user"
          ></v-text-field>
          <v-card
            height="52"
            outlined
            class="flex-grow-1 ml-2 chronology-datetime-content"
            style="border-color: #757575; max-width: 225px"
          >
            <div class="caption pl-2 pt-1">発生/確認日時</div>
            <datetime
              v-model="target.date"
              :max-datetime="currentDate"
              input-class="chronology-datetime-picker"
              type="datetime"
            >
              <template slot="button-confirm" slot-scope="scope">
                <span v-if="scope.step === 'date'">
                  <v-btn text color="indigo">時刻選択</v-btn>
                </span>
                <span v-if="scope.step === 'time'">
                  <v-btn text color="indigo">OK</v-btn>
                </span>
              </template>
            </datetime>
          </v-card>
        </div>

        <div class="d-flex flex-wrap mb-3">
          <v-select
            class="mr-3 pb-2"
            :items="statuses"
            label="対応状況"
            style="font-size: 14px; max-width: 110px"
            v-model="target.status"
            hide-details
          />
          <v-select
            class="mr-3 pb-2"
            :items="icons"
            label="アイコン"
            style="font-size: 14px; max-width: 100px"
            v-model="target.icon"
            hide-details
          >
            <template v-slot:selection="{ item }">
              <v-icon>{{ item }}</v-icon>
            </template>
            <template v-slot:item="{ parent, item }">
              <v-icon v-if="parent">{{ item }}</v-icon>
            </template>
          </v-select>
          <div style="min-width: 200px">
            <v-color-picker
              v-model="target.color"
              class="no-input"
              hide-canvas
              hide-mode-switch
              mode="hexa"
            ></v-color-picker>
          </div>
        </div>

        <v-text-field
          outlined
          label="件名"
          class="mb-3"
          style="font-size: 14px"
          v-model="target.notes"
          hide-details
          dense
        />

        <div class="d-flex my-2">
          <v-btn x-small outlined color="primary" @click="moveMapCenter()">
            マーカーを配置
          </v-btn>
          <v-btn
            x-small
            outlined
            color="primary"
            @click="moveDevCenter()"
            v-if="isDevelopment"
            class="ml-2"
          >
            中央
          </v-btn>
          <v-spacer />
          <div class="caption" v-if="target.lat && target.lng">
            {{ target.lat.toFixed(5) }}, {{ target.lng.toFixed(5) }}
          </div>
        </div>

        <v-card outlined class="mb-2">
          <LMap
            ref="chronologyMap"
            id="chronologyMap"
            :zoom="11"
            :center="center"
            :options="{ zoomControl: false, scrollWheelZoom: false }"
            :zoom-animation="false"
            style="height: 200px; width: 100%"
          >
            <LTileLayer
              url="https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png"
            />
            <LControl position="topright" v-if="leafletMap">
              <ZoomControl :map="leafletMap" />
            </LControl>
            <LMarker
              v-if="target.lat && target.lng"
              :lat-lng="[target.lat, target.lng]"
              draggable
              @dragend="dragend"
            />
          </LMap>
        </v-card>
      </v-card-text>
      <v-card-actions class="px-3">
        <v-btn color="error" text small @click="remove()" v-if="data">
          <v-icon>mdi-delete</v-icon>
          削除
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="$emit('close')">
          キャンセル
        </v-btn>
        <v-btn v-if="data" color="primary" @click="save()">
          保存
        </v-btn>
        <v-btn v-else color="primary" @click="saveAndProgress()">
          {{ "保存&状況報告" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import moment from "moment";
import { LMap, LTileLayer, LMarker, LControl } from "vue2-leaflet";
import { BRANCH_OFFICE } from "../../enums/BranchOffice";
import { Chronology } from "../../enums/Chronology";
import ZoomControl from "../base-map/layers/ZoomControl";
import chronologyAPI from "../../utils/chronologyAPI";
export default {
  name: "ChronologyEditor",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LControl,
    ZoomControl
  },

  props: {
    dialog: Boolean,
    data: Object,
    maxWidth: {
      type: [String, Number],
      default: 500
    }
  },

  data() {
    return {
      target: {},
      center: [BRANCH_OFFICE[0].lat, BRANCH_OFFICE[0].lng],
      currentLocation: [BRANCH_OFFICE[0].lat, BRANCH_OFFICE[0].lng],
      leafletMap: null,
      currentDate: moment().toISOString()
    };
  },

  computed: {
    categories() {
      return Chronology.categories;
    },
    sections() {
      return Chronology.sections;
    },
    statuses() {
      return Chronology.statuses;
    },
    icons() {
      return Chronology.icons;
    },
    userId() {
      return this.$store.getters.user.id;
    },
    isDevelopment() {
      return process.env.NODE_ENV === "development";
    }
  },

  watch: {
    dialog(val) {
      if (!val) return;
      this.load();
    }
  },

  created() {
    this.getCurrentLocation();
  },

  methods: {
    getCurrentLocation() {
      navigator.geolocation.getCurrentPosition(
        position => {
          this.currentLocation = [
            position.coords.latitude,
            position.coords.longitude
          ];
        },
        error => {
          console.log(error);
        }
      );
    },

    load() {
      if (!this.data) {
        const user = this.$store.getters.user;
        this.target = {
          id: 0,
          category: this.categories[0],
          section: this.sections[0],
          date: moment().toISOString(),
          user: user["custom:department"] + " " + user["custom:user_name"],
          notes: "",
          lat: this.currentLocation[0],
          lng: this.currentLocation[1]
        };
      } else {
        const d = moment.utc(this.data.date, "YYYYMMDDHHmm");
        this.target = { ...this.data, date: d.toISOString() };
      }

      if (!this.target.lat) {
        this.target.lat = BRANCH_OFFICE[0].lat;
      }
      if (!this.target.lng) {
        this.target.lng = BRANCH_OFFICE[0].lng;
      }
      if (!this.target.status) {
        this.target.status = this.statuses[0];
      }
      if (!this.target.icon) {
        this.target.icon = this.icons[0];
      }
      if (!this.target.color) {
        this.target.color = "#F44336B1";
      }
      setTimeout(() => {
        this.leafletMap = this.$refs.chronologyMap.mapObject;
        this.leafletMap.invalidateSize();
        this.center = [this.target.lat, this.target.lng];
        this.leafletMap.setView(this.center, 14);

        const container = this.$refs.chronologyContent;
        container.scrollTop = 0;
      }, 500);
    },

    async saveChronology() {
      const d = moment.utc(this.target.date);
      const params = {
        ...this.target,
        userId: this.userId,
        date: Number(d.format("YYYYMMDDHHmm"))
      };
      if (params.s3_url !== undefined) {
        delete params.s3_url;
      }
      const response = await chronologyAPI.post("/chronology", params);
      return response && response.success === true ? response : null;
    },

    async save() {
      const res = await this.saveChronology();
      if (res !== null) {
        this.$emit("close");
      }
    },

    async saveAndProgress() {
      const res = await this.saveChronology();
      if (res !== null) {
        this.$emit("closeAndProgress", {
          hash: res.hash,
          id: res.id,
          user: this.target.user
        });
      }
    },

    async remove() {
      if (!this.target || !this.target.id) return;
      if (!confirm("削除しますか？")) return;
      const params = { id: this.target.id };
      const response = await chronologyAPI.del("/chronology", params);
      if (response && response.success === true) {
        this.$emit("close");
      }
    },

    dragend(e) {
      const latlng = e.target.getLatLng();
      this.target.lat = latlng.lat;
      this.target.lng = latlng.lng;
    },

    moveMapCenter() {
      const latlng = this.leafletMap.getCenter();
      this.target.lat = latlng.lat;
      this.target.lng = latlng.lng;
    },

    moveDevCenter() {
      this.leafletMap.panTo([BRANCH_OFFICE[0].lat, BRANCH_OFFICE[0].lng]);
      this.target.lat = BRANCH_OFFICE[0].lat;
      this.target.lng = BRANCH_OFFICE[0].lng;
    }
  }
};
</script>

<style lang="scss">
.chronology-datetime-content {
  .chronology-datetime-picker {
    cursor: pointer;
    background-color: white;
    color: black;
    font-size: 12px;
    padding: 0;
    margin: 0;
    height: 22px;
    @media screen and (min-width: 900px) {
      font-size: 16px;
    }
  }
  .vdatetime-popup {
    z-index: 6002 !important;
  }
}
</style>

<style lang="scss">
.no-input {
  .v-color-picker__controls {
    padding: 12px;
    .v-color-picker__preview {
      .v-color-picker__sliders {
        .v-color-picker__hue {
          margin-bottom: 14px;
        }
      }
    }
    .v-color-picker__edit {
      margin: 0;
      .v-color-picker__input:last-child {
        display: none;
      }
    }
  }
}
</style>
